/** @jsxImportSource @emotion/react */
import {
  EuiButton,
  EuiButtonIcon,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHorizontalRule,
  EuiIcon,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiText,
  EuiTitle,
} from "@inscopix/ideas-eui";
import { useCallback, useMemo, useState } from "react";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { css } from "@emotion/react";
import { DataTable, DataTableRowData } from "pages/gdt/layout/DataTable";
import { GridApi, IRowNode, SelectionChangedEvent } from "ag-grid-community";
import { RowIdentifier } from "@inscopix/ideas-hyperformula";
import assert from "assert";
import { captureException } from "@sentry/react";

interface ModalFillSourceProps {
  onClose: () => void;
  tableId: string;
  sourceColId: string;
}

/**
 * Component that renders a modal for filling sources on an analysis table
 * takes a tableId for the analysis table, and a sourceColId for the source column being filled
 */
export const ModalFillSource = ({
  onClose,
  tableId,
  sourceColId,
}: ModalFillSourceProps) => {
  const selectedTable = useDataTableContext((s) =>
    s.tables.find((t) => t.id === tableId),
  );

  assert(selectedTable !== undefined);

  const [isLoading, setIsLoading] = useState(false);

  const selectedSourceTable = useDataTableContext((s) => {
    const selectedSourceCol = s.tables
      .flatMap((table) => table.columns)
      .find((col) => col.id === sourceColId);
    return s.tables.find((t) => t.id === selectedSourceCol?.key);
  });

  assert(selectedSourceTable !== undefined);

  const selectedSourceTableId = selectedSourceTable.id;

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [gridApi, setGridApi] = useState<GridApi<DataTableRowData>>();

  const handleSelectionChanged = useCallback(
    (e: SelectionChangedEvent<DataTableRowData>) => {
      const selectedRowIds = e.api.getSelectedRows().map((row) => row.id);
      setSelectedRowIds(selectedRowIds);
    },
    [setSelectedRowIds],
  );

  const unmappedSources = useMemo(() => {
    if (selectedTable === undefined) {
      return;
    }
    const sourceColGroupId = selectedTable.columnGroups.find(
      (col) => col.definition?.kind === "sources",
    )?.id;
    if (sourceColGroupId === undefined) {
      return;
    }
    const sourceColIdx = selectedTable.columns.findIndex(
      (col) =>
        col.group === sourceColGroupId &&
        col.definition.kind === "identifier" &&
        col.definition.table.id === selectedSourceTableId,
    );

    if (sourceColIdx < 0) {
      return;
    }

    const sourcesOnTable = selectedTable.rows.reduce<RowIdentifier[]>(
      (rowIdentifiers, curr) => {
        const cell = curr.cells[sourceColIdx];
        if (cell.value instanceof RowIdentifier) {
          rowIdentifiers.push(cell.value);
        }
        return rowIdentifiers;
      },
      [],
    );

    const sourceTableIdentifierColIdx = selectedSourceTable?.columns.findIndex(
      (c) => c.is_id_column,
    );
    if (sourceTableIdentifierColIdx < 0) {
      return;
    }

    return selectedSourceTable?.rows
      .map((r) => ({
        rowId: r.id,
        value: r.cells[sourceTableIdentifierColIdx].value,
      }))
      .filter(
        ({ value }) =>
          value instanceof RowIdentifier &&
          !sourcesOnTable?.some(
            (source) =>
              source.rowIndex === value.rowIndex &&
              source.tableKey === value.tableKey,
          ),
      ) as { rowId: string; value: RowIdentifier }[];
  }, [
    selectedSourceTable?.columns,
    selectedSourceTable?.rows,
    selectedSourceTableId,
    selectedTable,
  ]);

  const autoSelectSources = useCallback(() => {
    const nodes: IRowNode<DataTableRowData>[] = [];
    gridApi?.forEachNode((node) => {
      if (unmappedSources?.some((source) => source.rowId === node.id)) {
        nodes.push(node);
      }
    });
    gridApi?.deselectAll();
    nodes.forEach((node) => node.setSelected(true));
  }, [gridApi, unmappedSources]);

  const fillSources = useDataTableContext((s) => s.fillSources);

  const handleSubmit = async () => {
    const selectedSourceTableKind = selectedSourceTable?.kind;
    const body =
      selectedSourceTableKind === "analysis"
        ? { analysis_table_rows: selectedRowIds }
        : { data_table_rows: selectedRowIds };
    try {
      setIsLoading(true);
      await fillSources({
        analysisTableId: selectedTable?.id ?? "",
        ...body,
      });

      setIsLoading(false);
      addUtilityToastSuccess("Rows created");

      onClose();
    } catch (error) {
      addUtilityToastFailure("Failed to create analysis table");
      captureException(error);
      setIsLoading(false);
    }
  };

  const fillableSourcesCount = unmappedSources?.length ?? 0;

  return (
    <EuiModal
      onClose={onClose}
      css={css`
        width: 90vw;
        max-width: 90vw;
        height: 90vh;
        max-height: 90vh;
      `}
    >
      <EuiModalHeader>
        <EuiFlexGroup direction="column" gutterSize="none">
          <EuiModalHeaderTitle component="h3">Fill Sources</EuiModalHeaderTitle>

          <EuiHorizontalRule margin="none" />
        </EuiFlexGroup>
      </EuiModalHeader>

      <EuiModalBody>
        <EuiFlexGroup
          direction="column"
          gutterSize="s"
          style={{ height: "100%" }}
        >
          <EuiFlexItem grow={false}>
            <EuiCallOut>
              <EuiFlexGroup alignItems="center">
                <EuiFlexItem grow={false}>
                  <EuiButtonIcon
                    iconType="sparkles"
                    iconSize="xxl"
                    size="m"
                    color="primary"
                    style={{ alignSelf: "center" }}
                    isDisabled={fillableSourcesCount === 0}
                    onClick={autoSelectSources}
                  />
                </EuiFlexItem>
                <EuiFlexItem>
                  <EuiFlexGroup direction="column" gutterSize="xs">
                    <EuiTitle size="xs">
                      <EuiText size="m">{`Auto Select Sources Missing from ${selectedTable?.name ?? ""}`}</EuiText>
                    </EuiTitle>
                    <EuiFlexItem>
                      <EuiText>
                        {fillableSourcesCount > 0
                          ? `We will automatically select ${fillableSourcesCount}
                          source row(s) from ${selectedSourceTable.name} that are missing from your ${selectedTable.name} table`
                          : `Every row on the ${selectedSourceTable.name} source table has at least one filled as a source on your ${selectedTable.name} table. We can't auto select missing sources, but you can manually select sources to duplicate.`}
                      </EuiText>
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}></EuiFlexItem>
                  </EuiFlexGroup>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiButton
                    onClick={autoSelectSources}
                    disabled={fillableSourcesCount === 0}
                  >
                    <EuiIcon
                      type="sparkles"
                      color={fillableSourcesCount > 0 ? "primary" : undefined}
                    />
                    AutoSelect
                  </EuiButton>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiCallOut>
          </EuiFlexItem>
          <EuiFlexItem>
            <DataTable
              selectedTableId={selectedSourceTableId}
              handleSelectionChanged={handleSelectionChanged}
              isModalView
              setGridApi={setGridApi}
              gridApi={gridApi}
            />
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiModalBody>

      <EuiModalFooter>
        <EuiButton
          isLoading={isLoading}
          fill
          disabled={
            selectedSourceTableId === undefined || selectedRowIds.length < 1
          }
          onClick={() => void handleSubmit()}
        >
          Submit
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};
